import INVENTORY_API_BASE_URL from "../../config/api";
import React, { useState } from "react";
import axios from "axios";
import { useForm } from "react-hook-form";
import moment from "moment";
import "./SettlementSnap.css";

const SettlementSnapshot = () => {
  const { register, handleSubmit } = useForm();
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");

  const onSubmit = async (data) => {
    setLoading(true);
    setError("");
    setSnapshot(null);

    const formattedDate = moment(data.date).format("YYYY-MM-DD");

    try {
      const response = await axios.post(
        `${INVENTORY_API_BASE_URL}/searchSettlementSnapshot`,
        {
          date: formattedDate,
        }
      );
      setSnapshot(response.data);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to fetch data.");
    } finally {
      setLoading(false);
    }
  };

  const stockSummary = {
    A: { skus: new Set(), quantity: 0 },
    B: { skus: new Set(), quantity: 0 },
    C: { skus: new Set(), quantity: 0 },
    Scrap: { skus: new Set(), quantity: 0 },
  };

  if (snapshot?.SnapData?.length) {
    snapshot.SnapData.forEach((item) => {
      const category = item.bookCategory;
      if (stockSummary.hasOwnProperty(category)) {
        stockSummary[category].skus.add(item.sku);
        stockSummary[category].quantity += item.stock;
      }
    });
  }

  const filteredData = snapshot?.SnapData?.filter((batch) =>
    selectedCategory === "All" ? true : batch.bookCategory === selectedCategory
  );

  return (
    <main className="settlement-snapshot-page">
      <div className="settlement-snapshot__content">
        <header className="settlement-snapshot__header">
          <div>
            <p className="settlement-snapshot__eyebrow">Inventory snapshots</p>
            <h1>Settlement snapshot</h1>
            <p className="settlement-snapshot__subtitle">
              Choose a date to review settled stock quantities and values by category.
            </p>
          </div>
          <span className="settlement-snapshot__header-badge">Daily settlement</span>
        </header>

        <section className="settlement-snapshot__search-card" aria-label="Search settlement snapshot">
          <div className="settlement-snapshot__section-heading">
            <div>
              <h2>Search by date</h2>
              <p>Select the settlement date you want to inspect.</p>
            </div>
          </div>
          <form
            className="settlement-snapshot__search-form"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="settlement-snapshot__field">
              <label htmlFor="settlement-snapshot-date">Settlement date</label>
              <input
                id="settlement-snapshot-date"
                type="date"
                {...register("date", { required: true })}
              />
            </div>
            <button type="submit" className="settlement-snapshot__search-button">
              <span aria-hidden="true">⌕</span>
              Search snapshot
            </button>
          </form>
        </section>

        {loading && (
          <div className="settlement-snapshot__message" role="status">
            <span className="settlement-snapshot__spinner" aria-hidden="true" />
            <strong>Loading settlement snapshot…</strong>
            <span>Fetching the selected date’s inventory data.</span>
          </div>
        )}

        {error && (
          <div className="settlement-snapshot__error" role="alert">
            <strong>Snapshot could not be loaded</strong>
            <span>{error}</span>
          </div>
        )}

        {snapshot ? (
          <div className="settlement-snapshot__results">
            <section className="settlement-snapshot__summary" aria-label="Snapshot totals">
              <article className="settlement-snapshot__summary-card settlement-snapshot__summary-card--accent">
                <span className="settlement-snapshot__summary-label">Total settlement value</span>
                <strong>₹ {snapshot.Total.toFixed(2)}</strong>
                <span className="settlement-snapshot__summary-note">Snapshot total</span>
              </article>
              <article className="settlement-snapshot__summary-card">
                <span className="settlement-snapshot__summary-label">Book records</span>
                <strong>{snapshot.SnapData.length.toLocaleString()}</strong>
                <span className="settlement-snapshot__summary-note">Across all categories</span>
              </article>
              <article className="settlement-snapshot__summary-card">
                <span className="settlement-snapshot__summary-label">Snapshot date</span>
                <strong className="settlement-snapshot__date-value">
                  {moment(snapshot.Dated).format("DD-MM-YYYY")}
                </strong>
                <span className="settlement-snapshot__summary-note">Settlement date</span>
              </article>
            </section>

            <section className="settlement-snapshot__category-panel" aria-label="Category stock summary">
              <div className="settlement-snapshot__section-heading">
                <div>
                  <h2>Stock by category</h2>
                  <p>Unique SKU count and total stock in each category.</p>
                </div>
              </div>
              <div className="settlement-snapshot__category-grid">
                {Object.entries(stockSummary).map(([category, categoryData]) => (
                  <article className="settlement-snapshot__category-card" key={category}>
                    <span className="settlement-snapshot__category-label">{category}</span>
                    <strong>{categoryData.quantity.toLocaleString()}</strong>
                    <span className="settlement-snapshot__category-detail">
                      Quantity · {categoryData.skus.size.toLocaleString()} SKUs
                    </span>
                  </article>
                ))}
              </div>
            </section>

            <section className="settlement-snapshot__details-panel" aria-label="Settlement book details">
              <div className="settlement-snapshot__details-heading">
                <div>
                  <h2>Book details</h2>
                  <p>Browse the individual stock lines for this snapshot.</p>
                </div>
                <div className="settlement-snapshot__category-filter">
                  <label htmlFor="categorySelect">Book category</label>
                  <select
                    id="categorySelect"
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                  >
                    <option value="All">All categories</option>
                    <option value="A">A</option>
                    <option value="B">B</option>
                    <option value="C">C</option>
                    <option value="Scrap">Scrap</option>
                  </select>
                </div>
              </div>

              <div className="settlement-snapshot__table-scroll">
                <table className="settlement-snapshot__table">
                  <thead>
                    <tr>
                      <th scope="col">No.</th>
                      <th scope="col">SKU</th>
                      <th scope="col">Book category</th>
                      <th scope="col">Stock</th>
                      <th scope="col">Value (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.length === 0 ? (
                      <tr>
                        <td className="settlement-snapshot__empty" colSpan={5}>
                          <strong>No books in this category</strong>
                          <span>Choose another category to see its settlement lines.</span>
                        </td>
                      </tr>
                    ) : (
                      filteredData.map((batch, index) => (
                        <tr key={index}>
                          <td className="settlement-snapshot__serial-cell">{index + 1}</td>
                          <td className="settlement-snapshot__sku-cell">{batch.sku}</td>
                          <td><span className={"settlement-snapshot__category-tag category-" + String(batch.bookCategory || "").toLowerCase()}>{batch.bookCategory}</span></td>
                          <td className="settlement-snapshot__number-cell">{batch.stock}</td>
                          <td className="settlement-snapshot__value-cell">₹ {batch.value.toFixed(2)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <footer className="settlement-snapshot__table-footer">
                <span>{filteredData.length.toLocaleString()} book records</span>
                <span>Values are shown in Indian rupees.</span>
              </footer>
            </section>
          </div>
        ) : !loading && !error ? (
          <div className="settlement-snapshot__message settlement-snapshot__message--empty">
            <span className="settlement-snapshot__message-icon" aria-hidden="true">◷</span>
            <strong>Select a date to view a snapshot</strong>
            <span>The settlement summary and book details will appear here.</span>
          </div>
        ) : null}
      </div>
    </main>
  );
};

export default SettlementSnapshot;